import {Injectable} from '@angular/core';
import {ConfigService} from '@q9elements/ui-kit/common';

import {Q9maCookieService} from './cookie.service';
import {REDIRECT} from '../models/auth';

@Injectable({
  providedIn: 'root'
})
export class RedirectService {
  constructor(private cookieService: Q9maCookieService) {}

  private readonly environmentUrls: string[] = [
    ConfigService.environment.MAIN_APP_URL,
    ConfigService.environment.MCP_OAUTH_URL,
    ConfigService.environment.DIAGRAM_URL,
    ConfigService.environment.TEAM_APP,
    ConfigService.environment.REF_MODEL_APP,
    ConfigService.environment.RELEASE_URL,
    ConfigService.environment.DATA_STRUCTURE_URL,
    ConfigService.environment.REPORTS_APP,
    ConfigService.environment.ENTERPRISE_URL,
    ConfigService.environment.SYS_ADMIN_APP,
    ConfigService.environment.MAP_MANAGEMENT_APP,
    ConfigService.environment.API_ENDPOINT
  ].filter((url): url is string => !!url);

  /**
   * Replays a pending allowed redirect (?redirect= query param or the redirect cookie), if any.
   * OAuth redirects may leave MainApp, so they are replayed outside the Angular router.
   * Cookie cleanup is owned by RedirectGuard (canActivate); deleting the cookie here races
   * with post-login redirect consumption and drops the pending /authorize redirect.
   */
  public replayPendingRedirect(): boolean {
    const pendingRedirect =
      this.getAllowedRedirectFromLocation() || this.getAllowedRedirectFromCookie();

    if (!pendingRedirect) {
      return false;
    }

    window.location.href = pendingRedirect;

    return true;
  }

  /** `redirect` must already be percent-decoded; encoded values will not match the allowlist. */
  public allowed(redirect: string): boolean {
    return (
      this.environmentUrls.some(url => url && this.composeRegExp(url).test(redirect)) ||
      /^\/tasks+/.test(redirect)
    );
  }

  /**
   * Builds the post-login redirect from the redirect_uri param or the OAuth `state` blob,
   * with the auth code appended. Returns '' when there is no redirect or it is not allowed.
   */
  public buildAuthRedirectUrl(
    redirectUrl: string | null | undefined,
    state: string | null | undefined,
    token: string
  ): string {
    const url = redirectUrl || this.decodeStateRedirect(state);

    if (!url || !this.allowed(url)) {
      return '';
    }

    return this.appendAuthCode(url, token);
  }

  public appendAuthCode(url: string, token: string): string {
    const parsed = new URL(url, window.location.origin);

    parsed.searchParams.set('code', token);

    return parsed.toString();
  }

  public safeDecode(value: string): string | null {
    try {
      return decodeURIComponent(value);
    } catch {
      return null;
    }
  }

  private getAllowedRedirectFromLocation(): string {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get(REDIRECT);

    if (!redirect || !this.allowed(redirect)) {
      return '';
    }

    return redirect;
  }

  private getAllowedRedirectFromCookie(): string {
    const redirect = this.cookieService.getRedirect();

    return redirect && this.allowed(redirect) ? redirect : '';
  }

  private decodeStateRedirect(state: string | null | undefined): string {
    if (!state) {
      return '';
    }

    try {
      const decoded = JSON.parse(atob(decodeURIComponent(state)));

      if (typeof decoded['redirect_uri'] === 'string') {
        return decodeURIComponent(decoded['redirect_uri']);
      }

      return '';
    } catch {
      return '';
    }
  }

  private composeRegExp(url: string): RegExp {
    return new RegExp(`^${url.replace(/\//g, '\\/')}/.*`);
  }
}
